import React, { useMemo, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import MonthComparison from "./components/MonthComparison";
import FraudDetection from "./components/FraudDetection";
import { Button, Container, Nav, Navbar, Spinner } from "react-bootstrap";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import SalesAnalytics from "./components/SalesAnalytics";
import Filters from "./components/Filters";
import ClientSelector from "./components/ClientSelector";
import { AuthProvider, useAuth } from "./context/AuthContext";
import api from "./services/api";
import "./styles/App.css";
import ShopComparison from "./components/ShopComparison";

const createDefaultFilters = () => ({
  startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  endDate: new Date(),
  shopId: "",
});

const readStoredClient = (user) => {
  try {
    const stored = JSON.parse(localStorage.getItem("current_client") || "null");
    if (stored?.id) return stored;
  } catch {
    localStorage.removeItem("current_client");
  }

  if (!user?.client_id) return null;
  return {
    id: user.client_id,
    company_name: user.company_name || `Client ${user.client_id}`,
  };
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-loader">
        <Spinner animation="border" role="status" />
        <span>Checking authentication…</span>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const LoginRoute = () => {
  const { isAuthenticated, login } = useAuth();
  return isAuthenticated ? (
    <Navigate to="/dashboard" replace />
  ) : (
    <Login onLogin={login} />
  );
};

const PortalLayout = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [filters, setFilters] = useState(createDefaultFilters);
  const [currentClient, setCurrentClient] = useState(() =>
    readStoredClient(user),
  );

  const outletContext = useMemo(() => ({ filters }), [filters]);

  const handleFilterChange = (changes) => {
    setFilters((current) => ({ ...current, ...changes }));
  };

  const handleClientChange = (client) => {
    setCurrentClient(client);
    localStorage.setItem("current_client", JSON.stringify(client));
  };

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch {
      // Local logout should still complete when the API is unavailable.
    } finally {
      logout();
      navigate("/login", { replace: true });
    }
  };

  return (
    <div className="App">
      <Navbar expand="lg" sticky="top" className="portal-navbar">
        <Container fluid="xl">
          <Navbar.Brand as={NavLink} to="/dashboard">
            <span className="brand-mark">SA</span>
            <span>Sales Analytics</span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="portal-navigation" />
          <Navbar.Collapse id="portal-navigation">
            <Nav className="me-auto ms-lg-4">
              <Nav.Link as={NavLink} to="/dashboard">
                Overview
              </Nav.Link>
              <Nav.Link as={NavLink} to="/analytics">
                Advanced Analytics
              </Nav.Link>
              <Nav.Link as={NavLink} to="/fraud-detection">
                Fraud Risk
              </Nav.Link>
              <Nav.Link as={NavLink} to="/month-comparison">
                Month Comparison
              </Nav.Link>
              <Nav.Link as={NavLink} to="/shop-comparison">
                Shop Comparison
              </Nav.Link>
            </Nav>
            <div className="d-flex align-items-center gap-3 mt-3 mt-lg-0">
              <ClientSelector
                currentClient={currentClient}
                onClientChange={handleClientChange}
              />
              <div className="user-summary d-none d-md-block">
                <strong>{user?.username || user?.name || "User"}</strong>
                <small>
                  {user?.company_name || currentClient?.company_name}
                </small>
              </div>
              <Button variant="outline-danger" size="sm" onClick={handleLogout}>
                Sign out
              </Button>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <main className="portal-main">
        <Container fluid="xl">
          <Filters filters={filters} onFilterChange={handleFilterChange} />
          <Outlet context={outletContext} />
        </Container>
      </main>
    </div>
  );
};

const DashboardRoute = () => {
  const { filters } = useOutletContext();
  return <Dashboard filters={filters} />;
};
const ShopComparisonRoute = () => {
  const { filters } = useOutletContext();
  return <ShopComparison filters={filters} />;
};
const FraudDetectionRoute = () => {
  const { filters } = useOutletContext();

  return <FraudDetection filters={filters} />;
};

const AnalyticsRoute = () => {
  const { filters } = useOutletContext();
  return <SalesAnalytics filters={filters} />;
};

const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-loader">
        <Spinner animation="border" role="status" />
        <span>Loading portal…</span>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route
        element={
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/shop-comparison" element={<ShopComparisonRoute />} />
        <Route path="/dashboard" element={<DashboardRoute />} />
        <Route path="/analytics" element={<AnalyticsRoute />} />
        <Route path="/fraud-detection" element={<FraudDetectionRoute />} />
        <Route path="/month-comparison" element={<MonthComparison />} />
      </Route>
      <Route
        path="/"
        element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
        }
      />
      <Route
        path="*"
        element={
          <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
        }
      />
    </Routes>
  );
};

const App = () => (
  <BrowserRouter
    future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
  >
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  </BrowserRouter>
);

export default App;
